import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface ArticleFormProps {
  isOpen: boolean;
  onClose: () => void;
  onArticleCreated: () => void;
}

interface ArticleFormData {
  title: string;
  url: string;
  description: string;
  read: boolean;
}

const ArticleForm: React.FC<ArticleFormProps> = ({ isOpen, onClose, onArticleCreated }) => {
  const [formData, setFormData] = useState<ArticleFormData>({
    title: '',
    url: '',
    description: '',
    read: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiClient.post('/api/articles/', formData);
      onArticleCreated();
      onClose();
      setFormData({
        title: '',
        url: '',
        description: '',
        read: false,
      });
    } catch (error) {
      console.error('Error creating article:', error);
      alert('Failed to create article');
    }
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: 10 }} onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(107, 114, 128, 0.75)',
              transition: 'opacity',
            }}
          />
        </Transition.Child>

        <div style={{ position: 'fixed', inset: 0, zIndex: 10, overflowY: 'auto' }}>
          <div
            style={{
              display: 'flex',
              minHeight: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              padding: SPACING[16],
              textAlign: 'center',
            }}
          >
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                style={{
                  position: 'relative',
                  transform: 'none',
                  overflow: 'hidden',
                  borderRadius: RADIUS,
                  backgroundColor: COLORS.bg.panel,
                  padding: SPACING[24],
                  textAlign: 'left',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                  transition: 'all',
                  width: '100%',
                  maxWidth: '512px',
                }}
              >
                <div style={{ position: 'absolute', right: 0, top: 0, paddingRight: SPACING[16], paddingTop: SPACING[16] }}>
                  <button
                    type="button"
                    style={{
                      borderRadius: RADIUS,
                      backgroundColor: COLORS.bg.panel,
                      color: COLORS.text.tertiary,
                      border: 'none',
                      cursor: 'pointer',
                      padding: SPACING[4],
                    }}
                    onClick={onClose}
                  >
                    <span style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', borderWidth: 0 }}>Close</span>
                    <XMarkIcon style={{ height: '24px', width: '24px' }} aria-hidden="true" />
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <div style={{ marginTop: 0, textAlign: 'left', width: '100%' }}>
                    <Dialog.Title
                      as="h3"
                      style={{
                        fontSize: TYPOGRAPHY.headings.sizes.h2,
                        fontFamily: TYPOGRAPHY.headings.fontFamily,
                        fontWeight: TYPOGRAPHY.weight.semibold,
                        lineHeight: '1.5',
                        color: COLORS.text.primary,
                      }}
                    >
                      Add New Article
                    </Dialog.Title>
                    <form onSubmit={handleSubmit} style={{ marginTop: SPACING[16] }}>
                      <div style={{ marginBottom: SPACING[16] }}>
                        <label
                          htmlFor="title"
                          style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary,
                          }}
                        >
                          Title
                        </label>
                        <input
                          type="text"
                          id="title"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          style={{
                            marginTop: SPACING[4],
                            display: 'block',
                            width: '100%',
                            borderRadius: RADIUS,
                            border: `1px solid ${COLORS.border.default}`,
                            padding: `${SPACING[8]} ${SPACING[12]}`,
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            backgroundColor: COLORS.bg.panel,
                            color: COLORS.text.primary,
                            boxSizing: 'border-box',
                          }}
                          required
                        />
                      </div>

                      <div style={{ marginBottom: SPACING[16] }}>
                        <label
                          htmlFor="url"
                          style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary,
                          }}
                        >
                          Article URL
                        </label>
                        <input
                          type="url"
                          id="url"
                          value={formData.url}
                          onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                          style={{
                            marginTop: SPACING[4],
                            display: 'block',
                            width: '100%',
                            borderRadius: RADIUS,
                            border: `1px solid ${COLORS.border.default}`,
                            padding: `${SPACING[8]} ${SPACING[12]}`,
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            backgroundColor: COLORS.bg.panel,
                            color: COLORS.text.primary,
                            boxSizing: 'border-box',
                          }}
                          required
                        />
                      </div>

                      <div style={{ marginBottom: SPACING[16] }}>
                        <label
                          htmlFor="description"
                          style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary,
                          }}
                        >
                          Description
                        </label>
                        <textarea
                          id="description"
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows={3}
                          style={{
                            marginTop: SPACING[4],
                            display: 'block',
                            width: '100%',
                            borderRadius: RADIUS,
                            border: `1px solid ${COLORS.border.default}`,
                            padding: `${SPACING[8]} ${SPACING[12]}`,
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            backgroundColor: COLORS.bg.panel,
                            color: COLORS.text.primary,
                            boxSizing: 'border-box',
                            resize: 'vertical',
                          }}
                        />
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: SPACING[16] }}>
                        <input
                          type="checkbox"
                          id="read"
                          checked={formData.read}
                          onChange={(e) => setFormData({ ...formData, read: e.target.checked })}
                          style={{
                            height: '16px',
                            width: '16px',
                            borderRadius: RADIUS,
                            border: `1px solid ${COLORS.border.default}`,
                            accentColor: COLORS.accent.default,
                          }}
                        />
                        <label
                          htmlFor="read"
                          style={{
                            marginLeft: SPACING[8],
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            color: COLORS.text.secondary,
                          }}
                        >
                          Mark as read
                        </label>
                      </div>

                      <div style={{ marginTop: SPACING[24], display: 'flex', flexDirection: 'row-reverse', gap: SPACING[12] }}>
                        <button
                          type="submit"
                          style={{
                            display: 'inline-flex',
                            justifyContent: 'center',
                            borderRadius: RADIUS,
                            backgroundColor: COLORS.accent.default,
                            padding: `${SPACING[8]} ${SPACING[16]}`,
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontWeight: TYPOGRAPHY.weight.semibold,
                            color: '#FFFFFF',
                            border: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          Add Article
                        </button>
                        <button
                          type="button"
                          style={{
                            display: 'inline-flex',
                            justifyContent: 'center',
                            borderRadius: RADIUS,
                            backgroundColor: COLORS.bg.panel,
                            padding: `${SPACING[8]} ${SPACING[16]}`,
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontWeight: TYPOGRAPHY.weight.semibold,
                            color: COLORS.text.primary,
                            border: `1px solid ${COLORS.border.default}`,
                            cursor: 'pointer',
                          }}
                          onClick={onClose}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ArticleForm;
